// FlexParcelForm v2 — atomic components ported from the design handoff
// (ui/flex-parcel-form-handoff-v2/.../flex_atoms.jsx). All classNames map to
// styles/flex-form.css. Country data comes from @mnp/shared so we have one
// source of truth (44-country whitelist + phone codes).

import type { ReactNode, CSSProperties } from "react";
import { PARCEL_COUNTRIES, getCountry } from "@mnp/shared";
import { Icon } from "../ui/Icon.js";

// ──────────────────────────────────────────────────────────────
// Country presentation: flag + region grouping (UI-only — codes
// and phone prefixes already live in @mnp/shared/parcels/countries.ts).
// ──────────────────────────────────────────────────────────────
const FLAG_BY_CODE: Record<string, string> = {
  GE: "🇬🇪", RU: "🇷🇺", BY: "🇧🇾", AM: "🇦🇲", KZ: "🇰🇿",
  UA: "🇺🇦", MD: "🇲🇩", TR: "🇹🇷", UK: "🇬🇧", DE: "🇩🇪", FR: "🇫🇷",
  IT: "🇮🇹", ES: "🇪🇸", PL: "🇵🇱", NL: "🇳🇱", BE: "🇧🇪", AT: "🇦🇹",
  PT: "🇵🇹", IE: "🇮🇪", CZ: "🇨🇿", SK: "🇸🇰", HU: "🇭🇺", RO: "🇷🇴",
  BG: "🇧🇬", GR: "🇬🇷", HR: "🇭🇷", RS: "🇷🇸", BA: "🇧🇦", MK: "🇲🇰",
  AL: "🇦🇱", ME: "🇲🇪", XK: "🇽🇰", SI: "🇸🇮", NO: "🇳🇴", CH: "🇨🇭",
  IS: "🇮🇸", SE: "🇸🇪", FI: "🇫🇮", DK: "🇩🇰", EE: "🇪🇪", LV: "🇱🇻",
  LT: "🇱🇹", US: "🇺🇸", CA: "🇨🇦",
};

export function flagFor(code: string): string {
  return FLAG_BY_CODE[code] ?? "🏳️";
}

// ──────────────────────────────────────────────────────────────
// Input restrictors — block non-Latin input in name/address/zip fields
// (brief v2 bug 3). Used in onChange of all relevant text inputs.
// ──────────────────────────────────────────────────────────────

/**
 * Restricts input to Latin characters + digits + common punctuation.
 * Используется в onChange всех name/address/zip полей формы (brief v2 bug 3).
 * Allows: a-z A-Z 0-9 space . , - ' / ( )
 * Strips: cyrillic, emoji, anything else.
 */
export function restrictLatin(value: string): string {
  return value.replace(/[^a-zA-Z0-9 .,\-'/()]/g, "");
}

/** Stricter variant for human names (no digits, no comma/slash). */
export function restrictLatinName(value: string): string {
  return value.replace(/[^a-zA-Z .\-']/g, "");
}

/** Zip — alphanumeric + dash only. */
export function restrictZip(value: string): string {
  return value.replace(/[^a-zA-Z0-9 \-]/g, "");
}

/**
 * Restricts input to a Cyrillic name — used in onChange of all name fields of
 * the СНГ (CIS) corridor form. Allowed alphabet mirrors `isCyrillicName` in
 * @mnp/shared/parcels/cisFields.ts: русская кириллица (А-Яа-яЁё) + space and the
 * name punctuation . ' - (no digits, no comma/slash). Strips latin, emoji,
 * anything else, so the schema's cyrillic refine never trips on stray input.
 */
export function restrictCyrillicName(value: string): string {
  return value.replace(/[^А-Яа-яЁё .'\-]/g, "");
}

/**
 * Restricts input to a Cyrillic address — used in onChange of all CIS address
 * fields (city/street/building/apartment/zip). Allowed alphabet mirrors
 * `isCyrillicAddressText` in @mnp/shared/parcels/cisFields.ts: русская кириллица
 * (А-Яа-яЁё), digits, space and the address punctuation . , - / № ( ).
 */
export function restrictCyrillicAddress(value: string): string {
  return value.replace(/[^А-Яа-яЁё0-9 .,/№()\-]/g, "");
}

export type FlexRegionId = "hub" | "eu" | "na" | "ps";

export interface FlexRegion {
  id: FlexRegionId;
  label: string;
  match: (cat: string | undefined, code: string) => boolean;
}

export const FLEX_REGIONS: FlexRegion[] = [
  { id: "hub", label: "Хаб",                match: (_cat, code) => code === "GE" },
  { id: "eu",  label: "Европа",             match: (cat) => cat === "EUROPE" },
  { id: "na",  label: "Северная Америка",   match: (cat) => cat === "NORTH_AMERICA" },
  { id: "ps",  label: "Пост-советские",     match: (cat) => cat === "POST_SOVIET" },
];

export interface FlexCountryItem {
  code: string;
  name: string;
  flag: string;
  phoneCode: string;
  category: string;
}

export const FLEX_COUNTRIES: FlexCountryItem[] = PARCEL_COUNTRIES.map((c) => ({
  code: c.code,
  name: c.nameRu,
  flag: flagFor(c.code),
  phoneCode: c.phoneCode,
  category: c.category,
}));

export function flexCountry(code: string | undefined | null): FlexCountryItem | undefined {
  if (!code) return undefined;
  const c = getCountry(code);
  if (!c) return undefined;
  return { code: c.code, name: c.nameRu, flag: flagFor(c.code), phoneCode: c.phoneCode, category: c.category };
}

// ──────────────────────────────────────────────────────────────
// Hint primitives (?-button + collapsible panel)
// ──────────────────────────────────────────────────────────────
export interface HintProps {
  hints: Record<string, string>;
  openId: string | null;
  setOpenId: (v: string | null) => void;
}

export function FFHint({ id, hints, openId, setOpenId }: HintProps & { id: string }) {
  const text = hints[id];
  if (!text) return null;
  const open = openId === id;
  return (
    <button
      type="button"
      className="ff-hint-btn"
      data-open={open}
      onClick={(e) => {
        e.stopPropagation();
        setOpenId(open ? null : id);
      }}
      aria-label="Подсказка"
    >
      <Icon name="alertCircle" size={14} stroke={2} />
    </button>
  );
}

export function FFHintPanel({ id, hints, openId }: Omit<HintProps, "setOpenId"> & { id: string }) {
  if (openId !== id) return null;
  return <div className="ff-hint-panel">{hints[id]}</div>;
}

// ──────────────────────────────────────────────────────────────
// Section header (eyebrow + title + optional hint badge)
// ──────────────────────────────────────────────────────────────
export function FFSection({
  num,
  eyebrow,
  title,
  hint,
  children,
}: {
  num: string | number;
  eyebrow: string;
  title: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div className="ff-section">
      <div className="ff-section-head">
        <div>
          <p className="ff-section-eyebrow">
            <span className="num">{num}</span>
            {eyebrow}
          </p>
          <h3 className="ff-section-title">{title}</h3>
        </div>
        {hint && <span className="ff-section-hint">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Field wrapper: label + (?) + content + helper text
// ──────────────────────────────────────────────────────────────
export function FFField({
  id,
  label,
  required,
  optional,
  hints,
  openId,
  setOpenId,
  help,
  helpErr,
  children,
  dataField,
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  hints: Record<string, string>;
  openId: string | null;
  setOpenId: (v: string | null) => void;
  help?: ReactNode;
  helpErr?: ReactNode;
  children?: ReactNode;
  dataField?: string;
}) {
  return (
    <div className="ff-field" data-field={dataField}>
      <div className="ff-field-label">
        <span>
          {label}
          {required && <span className="req"> *</span>}
        </span>
        <FFHint id={id} hints={hints} openId={openId} setOpenId={setOpenId} />
        {optional && <span className="opt">необязательно</span>}
      </div>
      {children}
      {help && !helpErr && <p className="ff-field-help">{help}</p>}
      {helpErr && (
        <p className="ff-field-help err">
          <Icon name="alertCircle" size={12} className="ico" />
          {helpErr}
        </p>
      )}
      <FFHintPanel id={id} hints={hints} openId={openId} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Country select trigger — opens bottom sheet on tap
// ──────────────────────────────────────────────────────────────
export function FFCountrySelect({
  value,
  onClick,
  readonly = false,
  placeholder = "Выберите страну",
}: {
  value: string | undefined | null;
  onClick?: () => void;
  readonly?: boolean;
  placeholder?: string;
}) {
  const c = flexCountry(value);
  return (
    <button
      type="button"
      className={"ff-country-select" + (readonly ? " is-readonly" : "")}
      onClick={readonly ? undefined : onClick}
    >
      {c ? (
        <>
          <span className="flag">{c.flag}</span>
          <span className="name">{c.name}</span>
          <span className="code">{c.code}</span>
        </>
      ) : (
        <span className="name" style={{ color: "var(--mnp-gray-500)" }}>{placeholder}</span>
      )}
      {!readonly && (
        <svg
          className="caret"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      )}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Phone input — readonly prefix + local-number input
// ──────────────────────────────────────────────────────────────
export function FFPhone({
  countryCode,
  value,
  onChange,
  placeholder = "555 12 34 56",
  error = false,
  readonly = false,
}: {
  countryCode: string | undefined | null;
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  error?: boolean;
  readonly?: boolean;
}) {
  const c = flexCountry(countryCode);
  return (
    <div className={"ff-phone" + (error ? " is-error" : "")}>
      <span className="prefix">
        <span className="flag">{c?.flag ?? "🏳️"}</span>
        {c?.phoneCode ?? "+"}
      </span>
      <input
        type="tel"
        value={value}
        placeholder={placeholder}
        readOnly={readonly}
        onChange={(e) => onChange && onChange(e.target.value)}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Toggle row (iOS-style switcher)
// ──────────────────────────────────────────────────────────────
export function FFToggleRow({
  on,
  onChange,
  title,
  sub,
  hintId,
  hints,
  openId,
  setOpenId,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  title: string;
  sub?: string;
  hintId?: string;
  hints?: Record<string, string>;
  openId?: string | null;
  setOpenId?: (v: string | null) => void;
}) {
  return (
    <div className="ff-toggle-row">
      <div className="text">
        <p className="title">
          {title}
          {hintId && hints && openId !== undefined && setOpenId && (
            <span style={{ marginLeft: 6, verticalAlign: "middle", display: "inline-flex" }}>
              <FFHint id={hintId} hints={hints} openId={openId ?? null} setOpenId={setOpenId} />
            </span>
          )}
        </p>
        {sub && <p className="sub">{sub}</p>}
        {hintId && hints && openId !== undefined && (
          <FFHintPanel id={hintId} hints={hints} openId={openId ?? null} />
        )}
      </div>
      <button
        type="button"
        className="ff-toggle"
        data-on={on}
        onClick={() => onChange(!on)}
        aria-label={title}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Check row (square checkbox + title + sub)
// ──────────────────────────────────────────────────────────────
export function FFCheckRow({
  on,
  onChange,
  title,
  sub,
  hintId,
  hints,
  openId,
  setOpenId,
  style,
  dataField,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  title: ReactNode;
  sub?: ReactNode;
  hintId?: string;
  hints?: Record<string, string>;
  openId?: string | null;
  setOpenId?: (v: string | null) => void;
  style?: CSSProperties;
  dataField?: string;
}) {
  return (
    <div
      className="ff-check-row"
      style={style}
      onClick={() => onChange(!on)}
      role="checkbox"
      aria-checked={on}
      data-field={dataField}
    >
      <span className="ff-check" data-on={on}>
        {on && <Icon name="check" size={14} stroke={3} color="#fff" />}
      </span>
      <div className="text">
        <p className="title">
          {title}
          {hintId && hints && openId !== undefined && setOpenId && (
            <span style={{ marginLeft: 6, verticalAlign: "middle", display: "inline-flex" }}>
              <FFHint id={hintId} hints={hints} openId={openId ?? null} setOpenId={setOpenId} />
            </span>
          )}
        </p>
        {sub && <p className="sub">{sub}</p>}
        {hintId && hints && openId !== undefined && (
          <FFHintPanel id={hintId} hints={hints} openId={openId ?? null} />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Country picker bottom-sheet
// ──────────────────────────────────────────────────────────────
export function FFCountrySheet({
  open,
  title,
  selected,
  disabled = [],
  filterCodes,
  onClose,
  onPick,
}: {
  open: boolean;
  title: string;
  selected: string | undefined | null;
  disabled?: string[];
  filterCodes?: string[]; // если задан — показываем только эти страны
  onClose: () => void;
  onPick: (code: string) => void;
}) {
  if (!open) return null;
  // Locally: handle search in component state via uncontrolled input + useState below.
  return <FFCountrySheetInner
    title={title}
    selected={selected}
    disabled={disabled}
    filterCodes={filterCodes}
    onClose={onClose}
    onPick={onPick}
  />;
}

import { useState } from "react";

function FFCountrySheetInner({
  title,
  selected,
  disabled,
  filterCodes,
  onClose,
  onPick,
}: {
  title: string;
  selected: string | undefined | null;
  disabled: string[];
  filterCodes?: string[];
  onClose: () => void;
  onPick: (code: string) => void;
}) {
  const [query, setQuery] = useState("");
  const set = filterCodes ? new Set(filterCodes) : null;

  const items = FLEX_COUNTRIES.filter((c) => {
    if (set && !set.has(c.code)) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  });

  const groups = FLEX_REGIONS
    .map((r) => ({ region: r, items: items.filter((c) => r.match(c.category, c.code)) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <div className="ff-sheet-backdrop" onClick={onClose} />
      <div className="ff-sheet" style={{ top: 0 }}>
        <div className="ff-sheet-head">
          <div className="ff-sheet-grip" />
          <h3 className="ff-sheet-title">
            {title}
            <button className="close" aria-label="Закрыть" onClick={onClose}>
              <Icon name="close" size={18} />
            </button>
          </h3>
          <div className="ff-sheet-search">
            <Icon name="search" size={14} color="var(--mnp-fg-3)" />
            <input
              type="text"
              placeholder="Поиск страны…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <ul className="ff-sheet-list">
          {groups.map((g) => (
            <div key={g.region.id}>
              <li className="ff-sheet-section">
                {g.region.label} · {g.items.length}
              </li>
              {g.items.map((c) => {
                const isDisabled = disabled.includes(c.code);
                return (
                  <li
                    key={c.code}
                    className="ff-sheet-row"
                    data-selected={c.code === selected}
                    data-disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onPick(c.code);
                    }}
                  >
                    <span className="flag">{c.flag}</span>
                    <span className="nm">{c.name}</span>
                    {!isDisabled && <span className="ph">{c.phoneCode}</span>}
                  </li>
                );
              })}
            </div>
          ))}
          {groups.length === 0 && (
            <li style={{ padding: 24, textAlign: "center", color: "var(--mnp-fg-3)", fontSize: 13 }}>
              Ничего не найдено
            </li>
          )}
        </ul>
      </div>
    </>
  );
}
